// ============================================================================
// quorumHelper.js — Calcul du quorum live pour les réunions CA / AG / Bureau
// ============================================================================
// Refonte 2026-05-25. Centralise le calcul des voix théoriques (combien
// devraient être présents pour atteindre le quorum) et du quorum live à partir
// d'une réunion + des données de gouvernance.
//
// Règles statutaires Enio UGM (par défaut, éditables dans gouvernance.quorumRules) :
//   • CA     : quorum = 1/2          (régime unique)
//   • AG     : quorum simple = 1/4 · renforcé = 1/2
//   • Bureau : quorum = 1/2          (régime unique)
//
// Composition des voix théoriques (= référentiel statutaire) :
//   • CA     = délégués avec roles.includes("CA") et mandatCA actif
//   • AG     = délégués avec roles.includes("AG") et mandatAG actif
//   • Bureau = membres avec poste actif (gouvernance.postesActifs.length)
//
// IMPORTANT — Voix présentes (refonte 2026-05-25) :
//   Seuls les INVITÉS DE LA RÉUNION qui sont AUSSI membres officiels du quorum
//   (matchés à un délégué via email + rôle requis enregistré) sont comptés.
//   Les autres invités (observateurs, invités spéciaux, etc.) ne comptent pas
//   dans le quorum même s'ils sont présents.
//
//   Conséquence pour les invités hors-Aramis : pour être comptés dans le
//   quorum, ils doivent au préalable exister dans la table `delegates` avec
//   le rôle CA ou AG et un mandat actif. Sinon ils sont invités mais non
//   éligibles statutairement.
// ============================================================================

import { mandatOuvreDroit } from "./mandatsUtils";
import { delegatesPourSeance } from "./mutuellesSortantes";


const DEFAULT_QUORUM_RULES = {
  CA:     { simple: 0.5 },                   // 1/2 — régime unique pour le CA
  AG:     { simple: 0.25, renforce: 0.50 },  // 1/4 simple + 1/2 renforcé
  Bureau: { simple: 0.5 },                   // 1/2 — régime unique pour le Bureau
};

// Statuts considérés comme "présent" (compte dans le quorum)
const STATUTS_PRESENT = new Set(["confirme", "visio", "present", "accepte", "distanciel"]);

function normaliseStatut(s) {
  const v = String(s || "").toLowerCase().trim();
  return v;
}

/**
 * Retourne les seuils (ratios) à appliquer pour un type d'instance, en lisant
 * gouvernance.quorumRules avec fallback sur les valeurs par défaut.
 */
export function getQuorumThresholds(instanceType, gouvernance = {}) {
  const rules = gouvernance?.quorumRules || DEFAULT_QUORUM_RULES;
  return rules[instanceType] || DEFAULT_QUORUM_RULES[instanceType] || { simple: 0.5 };
}

/**
 * Indique si un délégué est membre officiel du quorum pour un type d'instance.
 * Source unique de vérité pour CA / AG / Bureau / CD.
 * Exporté Sprint 13 v3 pour permettre de déduire automatiquement les
 * observateurs côté UI (un participant non éligible au quorum = observateur,
 * sans code de vote ni voix dans le scrutin).
 */
export function delegateIsQuorumMember(delegate, instanceType, gouvernance) {
  if (!delegate) return false;
  const roles = delegate.roles || [];
  // mandatOuvreDroit : « sorti » ET « termine » (compte soft-supprimé) sont
  // exclus — avant, un mandat « termine » restait votant. L'absence d'objet
  // mandat est tolérée (fiches legacy « rôle sans mandat »).
  if (instanceType === "CA") {
    return roles.includes("CA") && mandatOuvreDroit(delegate.mandatCA);
  }
  if (instanceType === "AG") {
    return roles.includes("AG") && mandatOuvreDroit(delegate.mandatAG);
  }
  if (instanceType === "Bureau") {
    const postes = gouvernance?.postesActifs || [];
    return postes.some(p => p.membreId === delegate.id);
  }
  if (instanceType === "CD") {
    return roles.includes("CD");
  }
  return false;
}

/**
 * Calcule le nombre de voix théoriques selon le type d'instance.
 * = nombre de délégués éligibles au quorum.
 */
export function getVoixTheoriques(instanceType, { gouvernance = {}, delegates = [] } = {}) {
  return (delegates || []).filter(d => delegateIsQuorumMember(d, instanceType, gouvernance)).length;
}

/**
 * Indique si un invité d'une réunion est éligible au quorum :
 * - il doit être présent (statut confirme/visio)
 * - ET être matché à un délégué officiel ayant le rôle requis
 *
 * Match délégué : par email normalisé (lowercase + trim). Cas hors-Aramis :
 * un invité dont l'email ne match aucun délégué ne compte PAS dans le quorum
 * (même s'il est présent), car il n'est pas administrateur statutaire.
 */
function invitePeutCompter(invite, instanceType, { delegates = [], gouvernance = {} } = {}) {
  const email = (invite.email || "").toLowerCase().trim();
  if (!email) return false;
  const delegate = (delegates || []).find(d => (d.email || "").toLowerCase().trim() === email);
  if (!delegate) return false;
  return delegateIsQuorumMember(delegate, instanceType, gouvernance);
}

/**
 * Calcule le quorum live à partir d'une réunion et de son contexte.
 *
 * @param {object} reunion — doc reunion (avec invites[], instanceType, themisSessionId)
 * @param {object} ctx — { gouvernance, lists, delegates, themisSession }
 * @returns {object} {
 *   instanceType,
 *   voixTheoriques,        // N attendu
 *   voixPresentes,         // confirme/visio dans invites[]
 *   voixAvecPouvoirs,      // (AG uniquement) presentes + pouvoirs Thémis ; null sinon
 *   nbPouvoirs,            // (AG uniquement) nombre de pouvoirs comptés ; 0 sinon
 *   seuilSimple,           // ratio (ex. 0.25)
 *   seuilRenforce,         // ratio (ex. 0.50) ou null si pas de renforcé pour ce type
 *   voixRequisesSimple,    // ceil(voixTheoriques * seuilSimple)
 *   voixRequisesRenforce,  // ceil(voixTheoriques * seuilRenforce) ou null
 *   ratioSimple,           // ratio actuel (voixComptees / voixTheoriques)
 *   ratioRenforce,         // ratio actuel pour renforcé ou null
 *   quorumSimpleAtteint,   // boolean
 *   quorumRenforceAtteint, // boolean ou null
 *   manqueSimple,          // nb voix manquantes pour simple (0 si atteint)
 *   manqueRenforce,        // nb voix manquantes pour renforcé ou null
 *   pouvoirsInclus,        // true si on inclut les pouvoirs dans le calcul (AG seulement)
 * }
 */
export function calcQuorumLive(reunion, { gouvernance = {}, lists = [], delegates: annuaire = [], themisSession = null, nbPouvoirsThemis = null } = {}) {
  const instanceType = reunion?.instanceType || "";
  // Les fiches en exercice à la date de la réunion : une mutuelle sortie de
  // l'Union ne compte plus (décision de Pierre du 22/10/2026).
  const delegates = delegatesPourSeance(annuaire, { dateISO: reunion?.dateISO || "", instanceType }, lists);
  const thresholds = getQuorumThresholds(instanceType, gouvernance);
  const voixTheoriques = getVoixTheoriques(instanceType, { gouvernance, delegates });

  // Voix présentes : SEULS les invités qui sont
  //   1. présents (statut confirme/visio)
  //   2. ET membres officiels du quorum (matchés à un délégué via email + rôle CA/AG/Bureau actif)
  // Les autres invités (observateurs, invités externes) ne comptent pas dans le quorum.
  const invites = Array.isArray(reunion?.invites) ? reunion.invites : [];
  const invitesEligibles = invites.filter(i => invitePeutCompter(i, instanceType, { delegates, gouvernance }));
  const nbEligibles = invitesEligibles.length;
  const voixPresentes = invitesEligibles.filter(i => STATUTS_PRESENT.has(normaliseStatut(i.statut))).length;
  // Pour info / debug : nombre d'invités totaux et nombre d'invités hors-quorum
  const nbInvitesTotal = invites.length;
  const nbInvitesHorsQuorum = nbInvitesTotal - nbEligibles;

  // Pouvoirs Thémis : UNIQUEMENT pour AG, et UNIQUEMENT entre membres officiels.
  // On filtre les participants Thémis qui sont matchés à un délégué éligible
  // avant de compter leurs pouvoirs.
  const pouvoirsInclus = instanceType === "AG";
  let nbPouvoirs = 0;
  if (pouvoirsInclus && Number.isFinite(nbPouvoirsThemis)) {
    // Compte calculé depuis le REGISTRE themisPouvoirs par l'appelant
    // (syntheseParticipants.js) : avant la séance, participants[].pouvoirs
    // n'est pas alimenté — le bandeau de l'AG en comptait 8 pour 17 (23/09).
    nbPouvoirs = nbPouvoirsThemis;
  } else if (pouvoirsInclus && themisSession?.participants) {
    nbPouvoirs = themisSession.participants
      .filter(p => {
        // Le participant Thémis donne pouvoir uniquement s'il est lui-même
        // membre statutaire (sinon son pouvoir ne pèse pas).
        const email = (p.email || "").toLowerCase().trim();
        const delegate = (delegates || []).find(d => (d.email || "").toLowerCase().trim() === email);
        return delegateIsQuorumMember(delegate, instanceType, gouvernance);
      })
      .flatMap(p => Array.isArray(p.pouvoirs) ? p.pouvoirs : [])
      .length;
  }
  const voixAvecPouvoirs = pouvoirsInclus ? voixPresentes + nbPouvoirs : null;

  // Pour le décompte effectif (voix réellement comptées dans le quorum) :
  // - AG : présentes + pouvoirs
  // - autres : juste présentes
  const voixComptees = pouvoirsInclus ? voixAvecPouvoirs : voixPresentes;

  const seuilSimple = thresholds.simple ?? 0.5;
  const seuilRenforce = thresholds.renforce ?? null;

  const voixRequisesSimple = voixTheoriques > 0 ? Math.ceil(voixTheoriques * seuilSimple) : 0;
  const voixRequisesRenforce = seuilRenforce != null && voixTheoriques > 0
    ? Math.ceil(voixTheoriques * seuilRenforce)
    : null;

  const ratioSimple = voixTheoriques > 0 ? voixComptees / voixTheoriques : 0;
  const ratioRenforce = seuilRenforce != null && voixTheoriques > 0
    ? voixComptees / voixTheoriques
    : null;

  const quorumSimpleAtteint = voixComptees >= voixRequisesSimple && voixTheoriques > 0;
  const quorumRenforceAtteint = voixRequisesRenforce != null
    ? (voixComptees >= voixRequisesRenforce && voixTheoriques > 0)
    : null;

  const manqueSimple = Math.max(0, voixRequisesSimple - voixComptees);
  const manqueRenforce = voixRequisesRenforce != null
    ? Math.max(0, voixRequisesRenforce - voixComptees)
    : null;

  return {
    instanceType,
    voixTheoriques,
    voixPresentes,
    voixAvecPouvoirs,
    nbPouvoirs,
    nbInvitesTotal,
    nbInvitesHorsQuorum,
    seuilSimple,
    seuilRenforce,
    voixRequisesSimple,
    voixRequisesRenforce,
    ratioSimple,
    ratioRenforce,
    quorumSimpleAtteint,
    quorumRenforceAtteint,
    manqueSimple,
    manqueRenforce,
    pouvoirsInclus,
  };
}

/**
 * Détermine si une réunion est éligible à l'affichage du bandeau quorum.
 * Seulement les types instance officiels.
 */
export function isQuorumEligible(reunion) {
  // Le Bureau en est SORTI le 27/08/2026 : instance informelle, sans quorum
  // (décision Pierre). Seuls le CA et l'AG délibèrent statutairement.
  return ["CA", "AG"].includes(reunion?.instanceType);
}

/**
 * Label "1/4", "1/2" etc. pour afficher proprement un ratio.
 */
export function formatRatioLabel(r) {
  if (r === 0.25) return "1/4";
  if (r === 0.5) return "1/2";
  if (r === 0.75) return "3/4";
  if (r === 1) return "1/1";
  return `${Math.round(r * 100)}%`;
}
